import React from "react";
import { AlertTriangle, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Candidate } from "@/types/candidate";
import { candidateMergeConfig } from "@/config/candidateMergeConfig";
import DuplicateManager, { useDuplicateManager } from "@/components/common/DuplicateManager";
import { EntityMergeService } from "@/services/entityMergeService";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

interface DuplicateCandidatesBannerProps {
	candidate: Candidate;
	duplicates: Candidate[];
	className?: string;
	onMergeComplete?: () => void;
}

export const DuplicateCandidatesBanner = ({ 
	candidate, 
	duplicates, 
	className = "",
	onMergeComplete
}: DuplicateCandidatesBannerProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	// Prepare all candidates for merge (current + duplicates)
	const allCandidates = [candidate, ...duplicates];
	
	// Create a selection function that marks all as selected for merge
	const isSelected = () => true;

	const {
		openMergeModal,
		getDuplicateManagerProps
	} = useDuplicateManager(allCandidates, isSelected, "candidates", candidateMergeConfig);

	// Handle candidate merge
	const handleMerge = async (merged: Candidate, sources: Candidate[]) => {
		try {
			const result = await EntityMergeService.mergeCandidates(merged as any, sources as any);
			
			if (result.success) {
				toast({
					title: t("candidates.merge.success", "Candidates merged successfully"),
					description: t("candidates.merge.successDesc", `${sources.length} candidates have been merged into one.`),
				});
				if (result.redirectPath)
					navigate(result.redirectPath);
				
				// Trigger callback to refresh the page/data
				onMergeComplete?.();
				return result;
			} else {
				toast({
					title: t("candidates.merge.error", "Merge failed"),
					description: result.error || t("candidates.merge.errorDesc", "Failed to merge candidates"),
					variant: "destructive",
				});
				throw new Error(result.error || t("candidates.merge.errorDesc", "Failed to merge candidates"));
			}
		} catch (error) {
			console.error('Error merging candidates:', error);
			toast({
				title: t("candidates.merge.error", "Merge failed"),
				description: error instanceof Error ? error.message : t("candidates.merge.errorDesc", "Failed to merge candidates"),
				variant: "destructive",
			});
			throw error;
		}
	};

	if (duplicates.length === 0) {
		return null;
	}

	// Determine which fields caused the duplication
	const duplicateReasons: string[] = [];
	
	duplicates.forEach(dup => {
		if (candidate.email && dup.email && candidate.email.toLowerCase() === dup.email.toLowerCase()) {
			if (!duplicateReasons.includes('email')) {
				duplicateReasons.push('email');
			}
		}
		if (candidate.phone && dup.phone) {
			const candidatePhone = candidate.phone.replace(/[\s\-\(\)]/g, '');
			const dupPhone = dup.phone.replace(/[\s\-\(\)]/g, '');
			if (candidatePhone === dupPhone && !duplicateReasons.includes('phone')) {
				duplicateReasons.push('phone');
			}
		}
		if (candidate.linkedin && dup.linkedin) {
			const candidateLinkedIn = candidate.linkedin
				.replace(/https?:\/\/(www\.)?linkedin\.com\/in\//, '')
				.replace(/\/$/, '')
				.split('?')[0]
				.toLowerCase();
			
			const dupLinkedIn = dup.linkedin
				.replace(/https?:\/\/(www\.)?linkedin\.com\/in\//, '')
				.replace(/\/$/, '')
				.split('?')[0]
				.toLowerCase();

			if (candidateLinkedIn === dupLinkedIn && !duplicateReasons.includes('LinkedIn')) {
				duplicateReasons.push('LinkedIn');
			}
		}
	});

	const reasonText = duplicateReasons.length > 0 
		? t("duplicates.reasons") + " " + `based on ${duplicateReasons.join(', ')}`
		: '';

	return (
		<div className={`w-full bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 ${className}`}>
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<AlertTriangle className="h-4 w-4 text-amber-600" />
					<div className="flex items-center gap-2">
						<p className="text-xs font-medium text-amber-900">
							{t("duplicates.detected", "Potential duplicates detected")}
						</p>
						<div className="flex items-center gap-1 text-amber-700">
							<Users className="h-3 w-3" />
							<span className="text-xs">
								{duplicates.length} {duplicates.length === 1 
									? t("duplicates.candidate", "candidate") 
									: t("duplicates.candidates", "candidates")
								} {reasonText}
							</span>
						</div>
					</div>
				</div>
				<Button
					size="sm"
					variant="outline"
					onClick={openMergeModal}
					className="flex items-center gap-1 text-xs h-7 px-2 border-amber-300 text-amber-700 hover:bg-amber-100"
				>
					<Users className="h-3 w-3" />
					{t("duplicates.mergeAction", "Merge Entities")}
				</Button>
			</div>			
			{/* Render the DuplicateManager modal */}
			<DuplicateManager {...getDuplicateManagerProps(handleMerge)} />
		</div>
	);
};
